import { Prop } from 'vue-property-decorator';
import { State } from 'vuex-class';
import { Validation } from 'vuelidate';
import ordinal from 'ordinal';
import Vue from 'vue';

import Location from '@/services/Location';
import { Phone } from '@/services/Location';

import ListActions from '@/components/DraggableCardList/ListActions';
import { indexOf } from 'lodash';

interface SimpleStringMap {
	[key: string]: string;
}

interface PhonesValidation extends Validation {
	$each: Array<Validation & { [key: string]: unknown }>;
	isUnique: boolean;
}

export default abstract class ContactNumbersSection extends Vue {
	@State('location') location!: Location;
	@Prop({}) readonly validation!: PhonesValidation;
	
	/**
	 * A blank item with default values
	 */
	readonly blank = {
		'name': '',
		'value': '',
	};

	lastTouched = -1;

	protected getCardTitle(index: number): string {
		return ordinal(index + 1);
	}

	protected dereference(list: Array<Phone>): Array<Phone> {
		return list.map((a: Phone): Phone => {
			return {...a} as Phone;
		});
	}

	get phonesList(): Array<object> {
		return this.location.phones as Array<object>;
	}

	set phonesList(list: Array<object>) {
		this.location.phones;
	}
	
	get phoneNames(): Array<string> {
		return this.location.phones.map(x => x.name.toLowerCase());
	}

	get phoneNumbers(): Array<string> {
		return this.location.phones.map(x => x.value.toLowerCase());
	}

	/**
	 * Get a list of validation errors
	 * @param macros Object of macro key-value pairs, no mustache eg {'name': 'Day(s)'}
	 */
	getValidationErrors(index: number, key: string, macros: SimpleStringMap): Array<string> {
		return this.getMacroErrorMessages(index, key).map((message) => {
			return this.formatMacroMessage(message, macros);
		});
	}

	/**
	 * Mustache-formatted error messages for this phones list
	 */
	getMacroErrorMessages(index: number, key: string): Array<string> {
		const item = this.validation.$each?.[index]?.[key] as PhonesValidation;

		if (!item || !item.$dirty) {
			return [];
		}

		const errors = [];

		if (key == 'value' && !item.isUnique && index > indexOf(this.phoneNumbers, item.$model.toLowerCase())) {
			errors.push('This phone number already exists');
		}

		if (key == 'name' && !item.isUnique && index > indexOf(this.phoneNames, item.$model.toLowerCase())) {
			errors.push('A phone number with this name already exists');
		}

		if ('required' in item.$params && item.$invalid && item.$model.length < 1) {
			errors.push('Please complete this field');
		}
		
		return errors;
	}

	/**
	 * Convert mustache
	 */
	formatMacroMessage(message: string, macros: SimpleStringMap): string {
		if (!message) {
			return '';
		}

		const splitter = /{{\s*(.+?)\s*}}/gi;

		return message.replace(splitter, (full: string, macro: string): string => {
			if (macro in macros) {
				return macros[macro] as string;
			}
			return full;
		});
	}

	touch(index: number, key: string) {
		const item = this.validation?.$each?.[index]?.[key] as Validation;
		item?.$touch();
	}

	updateLastTouched(i: number) {
		this.lastTouched = i;
	}

	getLastTouched() {
		return this.lastTouched;
	}

	/**
	 * Add a blank item to the phones list
	 */
	add() {
		this.phonesList = ListActions.add(this.phonesList, this.blank);
	}

	/**
	 * Move an existing item in the phones list
	 */
	move(fromIndex: number, toIndex: number) {
		this.phonesList = ListActions.move(this.phonesList, fromIndex, toIndex);
		this.updateLastTouched(toIndex);
	}

	/**
	 * Removes all blank entries from the location.phones array
	 */
	removeBlank() {
		this.location.phones = this.location.phones.filter(x => x.name != '' || x.value != '')
	}

	/**
	 * Delete an existing item from the phones list
	 * (Note: "delete" is a reserved word in JS)
	 */
	remove(index: number) {
		this.phonesList = ListActions.remove(this.phonesList, index);
		if (this.phonesList.length === 0) {
			this.add();
		}
	}
}
